import React, { useEffect, useState } from 'react';
import { DbQuestionData, Player, DetailedPlayer, Mode } from '../room/types';
import {
  Box,
  Button,
  Stack,
  useTheme,
  Container,
  IconButton,
  Typography,
} from '@mui/material';
import { LeaderboardDrawer } from '../room/game/score-screen/LeaderboardDrawer';
import { ScorecardMulti } from '../room/game/scorecard-multi';
import { fetchGet } from '../../api/common';
import { useNavigate, useParams } from 'react-router-dom';
import { assignRanks } from '../room/utils';
import { FaDiscord, FaTwitter } from 'react-icons/fa';
import { SEO } from '../../SEO';
import { isMobile } from 'react-device-detect';
import { ScorecardSmall } from '../room/game/scorecard-multi/ScorecardSmall';

const poolLeaderboardDrawerWidth = 280;
export const HEADER_HEIGHT = 40;

export const ScoreScreenPublic = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const [mode, setMode] = useState<Mode>();
  const [handicap, setHandicap] = useState<number>();
  const [variant, setVariant] = useState<
    'normal' | 'survival' | 'starter-kit' | 'turbo'
  >();
  const [seededBy, setSeededBy] = useState<string>();
  const [detailedPlayers, setDetailedPlayers] =
    useState<Record<string, DetailedPlayer>>();
  const [sortedPlayers, setSortedPlayers] = useState<Player[]>();
  const [selectedPlayer, setSelectedPlayer] = useState<DetailedPlayer>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [showDailiesMessage, setShowDailiesMessage] = useState(false);

  useEffect(() => {
    fetchGame();
  }, []);

  const fetchGame = async () => {
    const res = await fetchGet(`/games/public/result/${gameId}`);
    if (!res) return;
    if (res.message === 'Cannot share dailies') {
      setShowDailiesMessage(true);
      return;
    }
    setMode(res.game.mode);
    setHandicap(res.game.handicap);
    setVariant(res.game.variant);
    setSeededBy(res.game.matchMakingSeededBy);
    setDetailedPlayers(res.game.players);
    setSortedPlayers(sortPlayers(res.game.players));
    setQuestionSet(res.questionSet);
  };

  const handlePoolLeaderboardUsernameClick = (profileId: string) => {
    if (detailedPlayers) setSelectedPlayer(detailedPlayers[profileId]);
  };

  const sortPlayers = (players: Record<string, Player>) => {
    const sortedPlayers = Object.values(players).sort(
      (a, b) => a.parScore - b.parScore
    );
    return assignRanks(sortedPlayers);
  };

  const handleJoinNowClick = () => navigate('/signup');

  const smallScorecard = (
    <Container>
      <Stack
        spacing={2}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 2,
        }}
      >
        <Box
          display={'flex'}
          justifyContent={'center'}
          height={`${HEADER_HEIGHT}px`}
        >
          <Box width={'30%'}>
            <a href="https://par.live">
              <img
                alt="PAR"
                src="/glowing-0.svg"
                className="par-logo-dark-crooked"
              />
            </a>
          </Box>
          <Box width={'40%'} display={'flex'} justifyContent={'center'}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleJoinNowClick}
              size="small"
            >
              PLAY FREE NOW!
            </Button>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            width={'30%'}
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <Stack direction="row" alignItems="center">
              <IconButton
                color="primary"
                href="https://discord.gg/upX48KPfHd"
                target="_blank"
                sx={{ mr: 1 }}
              >
                <FaDiscord size="1em" />
              </IconButton>
              <IconButton
                color="primary"
                href="https://twitter.com/ParTrivia"
                target="_blank"
                sx={{ mr: 1 }}
              >
                <FaTwitter size="1em" />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ paddingX: 0, paddingTop: 0 }}>
          {showDailiesMessage ? (
            <Typography
              variant="h6"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              Dailies are not shareable
            </Typography>
          ) : null}
          {detailedPlayers && mode && questionSet ? (
            <ScorecardSmall
              detailedPlayers={detailedPlayers}
              forPublic={true}
              hideUsernameLink={true}
              mode={mode}
              questionSet={questionSet}
              selectedPlayer={selectedPlayer}
            ></ScorecardSmall>
          ) : null}
        </Box>
      </Stack>
    </Container>
  );

  const scoreCard = (
    <Container maxWidth="xl">
      <Box
        sx={{
          position: 'absolute',
          marginTop: 4,
          marginLeft: 2,
        }}
      >
        <a href="https://par.live">
          <img alt="PAR" src="/glowing-0.svg" className="par-logo-dark-large" />
        </a>
      </Box>
      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 2,
          width: `calc(100% - ${
            mode === 'sit-n-go' ? poolLeaderboardDrawerWidth : 0
          }px)`,
        }}
      >
        <Box display={'flex'} justifyContent={'center'}>
          <Box width={'30%'}></Box>
          <Box width={'40%'} display={'flex'} justifyContent={'center'}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleJoinNowClick}
              size="large"
              sx={{ fontSize: '20px' }}
            >
              PLAY FREE NOW!
            </Button>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            width={'30%'}
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <Stack direction="row" alignItems="center">
              <IconButton
                color="primary"
                href="https://discord.gg/upX48KPfHd"
                target="_blank"
                sx={{ mr: 1 }}
              >
                <FaDiscord size="1.2em" />
              </IconButton>
              <IconButton
                color="primary"
                href="https://twitter.com/ParTrivia"
                target="_blank"
                sx={{ mr: 1 }}
              >
                <FaTwitter size="1.2em" />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ paddingX: 2, paddingTop: 2 }}>
          {showDailiesMessage ? (
            <Typography
              variant="h4"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              Dailies are not shareable
            </Typography>
          ) : null}
          {detailedPlayers && mode && questionSet ? (
            <ScorecardMulti
              detailedPlayers={detailedPlayers}
              forPublic={true}
              handicap={handicap}
              hideUsernameLink={true}
              mode={mode}
              questionSet={questionSet}
              seededBy={seededBy}
              selectedPlayer={selectedPlayer}
              variant={variant}
            ></ScorecardMulti>
          ) : null}
        </Box>
      </Stack>
      {mode === 'sit-n-go' ? (
        <LeaderboardDrawer
          gameOver={true}
          handlePoolLeaderboardUsernameClick={
            handlePoolLeaderboardUsernameClick
          }
          poolLeaderboardDrawerWidth={poolLeaderboardDrawerWidth}
          profileId={''}
          roundOver={true}
          sortedPlayers={sortedPlayers}
          showClickTip
        ></LeaderboardDrawer>
      ) : null}
    </Container>
  );

  return (
    <>
      <SEO
        title="PAR"
        description="Scorecard"
        name="PAR"
        type="website"
        imageUrl="https://res.cloudinary.com/dqlqwdqs9/image/upload/v1684725294/PAR_scorecard_thumbnail_g9kj0p.jpg"
      />
      {isMobile ? smallScorecard : scoreCard}
    </>
  );
};
